import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import get from 'lodash.get';
import React from 'react';

import FormBanner from '../components/Common/FormBanner';
import IframeModal from '../components/Common/IframeModal';
import { ArrowLink } from '../components/Common/Links';
import PageSection from '../components/Common/PageSection';
import SmartLink from '../components/Common/SmartLink';
import Layout from '../components/Layouts/Layout';
import LearningCenterSeo from '../components/LearningCenter/LearningCenterSeo';
import Pager from '../components/Resources/Pager';
import ResourceFilters from '../components/Resources/ResourceFilters';
import * as styles from './resources.module.css';

const getImage = (post) => {
  if (!post) return null;

  let ogImage = post.metaData?.ogImage || post.pageMeta?.ogImage || post.pageMetaData?.ogImage;

  // HACK to get og Image
  if (post.postType === 'customer success landing template___NODE') {
    ogImage = post.image;
  }
  if (ogImage && ogImage.file?.contentType === 'image/svg+xml') {
    return <img src={ogImage.file.url} alt={ogImage?.description} />;
  } else if (ogImage && ogImage.gatsbyImageData) {
    return <GatsbyImage data-ot-ignore alt={ogImage.description} image={ogImage.gatsbyImageData} />;
  } else if (ogImage && ogImage.resize) {
    return <img src={ogImage.resize.src} alt={ogImage?.description} />;
  }

  const heroImg = post.heroImage || post.image;
  if (heroImg?.file?.contentType === 'image/svg+xml') {
    return <img src={heroImg.file.url} alt={heroImg?.description} />;
  } else if (heroImg?.gatsbyImageData) {
    return <GatsbyImage data-ot-ignore alt={heroImg.description} image={heroImg.gatsbyImageData} />;
  } else if (heroImg && heroImg?.resize) {
    return <img src={heroImg.resize.src} alt={heroImg?.description} />;
  }
  return null;
};

const LearningCenter = (props) => {
  const { page, pillarPages, blogSettings } = props.data;
  const { pageContext, location } = props;
  const locationPath = location?.pathname?.split('/').filter((p) => p !== '');

  // Determine if pillar cards should be filtered:
  const hasValue = locationPath.length > 1;
  const edge = hasValue && locationPath[1];
  const filter = hasValue && locationPath[2];
  let pillars = pillarPages?.nodes;
  if (hasValue) {
    pillars = pillars.filter((p) => {
      if (edge === 'roles') {
        return p.roles?.find((r) => r.slug === filter);
      }
      if (edge === 'industries') {
        return p.industries?.find((i) => i.slug === filter);
      }
      if (edge === 'content') {
        return p.contentType?.slug === filter;
      }
      return false;
    });
  }

  return (
    <Layout pageMeta={page.pageMetaData}>
      <LearningCenterSeo meta={page.pageMeta} slug={props.location.pathname} />
      <PageSection isTopSection={true} className={styles.grey} slim>
        <div className={styles.hero}>
          <div>
            <h1>{page.heading}</h1>
            <h2>{page.subheading}</h2>
          </div>
        </div>
        <ResourceFilters location={location} />
        <nav className={styles.nav}>
          <h4>Latest Posts</h4>
        </nav>
        <div className={styles.cards}>
          {pageContext.posts.map((post, i) => {
            const img = getImage(post);
            const isReprise = post.ctaExternalLink?.startsWith('https://app.getreprise.com/');
            const cardContent = (
              <>
                <div className={styles.cardImage}>{img}</div>
                {post.contentType?.title && <div className={styles.cardType}>{post.contentType?.title}</div>}
                {post.title && <h4>{post.title}</h4>}
                <p>{post.description}</p>
                <div className={styles.postLink}>
                  {isReprise ? (
                    <ArrowLink label={post.contentType?.viewLabel} />
                  ) : (
                    <ArrowLink
                      label={post.contentType?.viewLabel || 'Read more'}
                      url={post.ctaExternalLink || post.fullSlug}
                      isExternal={!!post.ctaExternalLink}
                    />
                  )}
                </div>
              </>
            );
            if (!!post.ctaExternalLink) {
              // Open Reprise links in a modal iframe rather than link out
              if (isReprise) {
                const clickTarget = (
                  <div key={i} className={styles.postCard}>
                    {cardContent}
                  </div>
                );
                return <IframeModal content={post} children={clickTarget} />;
              } else {
                return (
                  <SmartLink key={i} className={styles.postCard} to={post.ctaExternalLink} isExternal="true">
                    {cardContent}
                  </SmartLink>
                );
              }
            }
            return (
              <SmartLink key={i} className={styles.postCard} to={post.fullSlug}>
                {cardContent}
              </SmartLink>
            );
          })}
        </div>
        <div className={styles.pager}>
          <Pager
            className={styles.pagerSet}
            currentPage={pageContext.pageNumber}
            next={pageContext.nextPage}
            previous={pageContext.prevPage}
            pageDisplay={pageContext.pageDisplay}
            baseUrl={pageContext.basePagePath}
          />
        </div>
        {pillars && pillars.length > 0 && (
          <nav className={styles.nav}>
            <h4>{page.additionalResourcesTitle}</h4>
          </nav>
        )}
        {pillars && pillars.length > 0 && (
          <div className={styles.cards}>
            {pillars.map((post, i) => {
              const img = getImage(post);

              return (
                <SmartLink key={i} className={styles.postCard} to={`/${post.slug}`}>
                  <div className={styles.cardImage}>{img}</div>
                  {post.contentType?.title && <div className={styles.cardType}>{post.contentType?.title}</div>}
                  {post.heading && <h4>{post.heading}</h4>}
                  <p>{post.description?.description}</p>
                  <div className={styles.postLink}>
                    <ArrowLink label={post.contentType?.viewLabel || 'Read more'} url={'/' + post.slug} />
                  </div>
                </SmartLink>
              );
            })}
          </div>
        )}
      </PageSection>
      <FormBanner
        slim
        className={styles.grey}
        heading={blogSettings.newsletterSignupHeading}
        formId={blogSettings.marketoFormId}
        form={blogSettings.form}
        successMessage={get(blogSettings, 'form.successMessage')}
      />
    </Layout>
  );
};

export default LearningCenter;

export const pageQuery = graphql`
  query($pageId: String!) {
    blogSettings: contentfulBlogSettings(contentful_id: { eq: "6KdgnoVe8iL9MGRTTPgqyQ" }) {
      ...SiteSettings
    }
    page: contentfulResourcesHomepage(id: { eq: $pageId }) {
      ...ResourcesHomepage
    }
    pillarPages: allContentfulBlogPage(
      filter: { templateStyle: { eq: "Pillar Page" } }
      sort: { fields: updatedAt, order: DESC }
    ) {
      nodes {
        slug
        heading
        description {
          description
        }
        publishDate
        updatedAt
        pageMetaData {
          ... on Node {
            ...BlogMeta
          }
        }
        heroImage {
          file {
            url
            contentType
          }
          gatsbyImageData
        }
        contentType {
          title
          viewLabel
          slug
        }
      }
    }
  }
`;
